<template>
	<div class="selection-view">
		<div class="newsletter-header">
			<p class="title">{{ getString('create_email_with_ai') }}</p>
			<p class="subtitle" v-html="getString('create_email_with_ai_explain')"></p>
		</div>
		<NewsletterAiInput
			:value="typedMessage || ''"
			:placeholder="getString('describe_email_campaign')"
			:loading="isLoading"
			:attachment-count="newsletterStore.attachedImages.length"
			:thinking-mode="newsletterStore.thinkingMode"
			@update:value="typedMessage = $event"
			@update:thinking-mode="newsletterStore.thinkingMode = $event"
			@send="generateEmail"
			@add-attachment="addAttachment"
		/>
		<div class="newsletter-list-card">
			<AppCard v-if="newsletterStore.businessNewsletters && newsletterStore.businessNewsletters.length > 0">
				<template #header>
					<h3>{{ getString('your_newsletters') }}</h3>
					<div>
						<AppButtonIcon
							prevent-change
							icon-name="icon-filters"
							:active="newsletterStore.hasActiveFilters"
							@click-icon="toggleFilterBottomSheet"
						/>
					</div>
				</template>
				<template #body>
					<div class="newsletters-list">
						<NewsletterSelectionItem
							v-for="newsletter in displayedNewsletters"
							:key="newsletter.business_newsletter_id"
							:newsletter="newsletter"
							@click="openNewsletter(newsletter.business_newsletter_id)"
						/>
					</div>
					<PaginationRow
						class="mt-10"
						:page="page"
						:page-size="pageSize"
						:total="newsletterStore.businessNewsletters.length"
						:initial-page-size="3"
						:page-size-options="['3', '9']"
						@page-changed="pageChanged"
					/>
				</template>
			</AppCard>
		</div>
		<NewsletterTemplatesList :loading="isLoading" @generate-newsletter="generateFromTemplate" />
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="showFilterBottomSheet" @toggling-popup="closeFilterBottomSheet">
			<div v-if="showFilterBottomSheet">
				<ModalHeader :title="getString('newsletter_filters')" />
				<AppList :items="filterItems" />
			</div>
		</AppBottomSheet>
		<RechargeBottomSheet
			:active="showRechargeBottomSheet"
			@close="handleRechargeClose"
		/>
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppList from "@/shared/components/AppList.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import RechargeBottomSheet from "@/console-new/components/shared/RechargeBottomSheet.vue";
import NewsletterAiInput from "@/console-new/components/marketing/NewsletterAiInput.vue";
import NewsletterSelectionItem from "@/console-new/components/marketing/NewsletterSelectionItem.vue";
import NewsletterTemplatesList from "@/console-new/components/marketing/NewsletterTemplatesList.vue";
import PaginationRow from "@/console-new/components/shared/PaginationRow.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useUserStore} from "@/user/stores";
import {useNewsletterStore} from "@/console-new/stores/newsletter.store";
import API from "@/shared/helpers/API";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let notificationStore = useNotificationStore();
		let userStore = useUserStore();
		let newsletterStore = useNewsletterStore();
		return {
			getString: languageStore.getString,
			showNotification: notificationStore.showNotification,
			userStore,
			newsletterStore,
		};
	},
	name: "NewsletterSelection",
	emits: ["add-attachment"],
	mixins: [WindowWidth],
	components: {
		AppCard,
		AppButtonIcon,
		AppBottomSheet,
		AppList,
		ModalHeader,
		RechargeBottomSheet,
		NewsletterAiInput,
		NewsletterSelectionItem,
		NewsletterTemplatesList,
		PaginationRow
	},
	data() {
		return {
			typedMessage: null,
			isLoading: false,
			page: 0,
			pageSize: 3,
			showFilterBottomSheet: false,
			showRechargeBottomSheet: false
		}
	},
	computed: {
		displayedNewsletters() {
			if (!this.newsletterStore.businessNewsletters) {
				return [];
			}
			return this.newsletterStore.businessNewsletters.slice(this.page * this.pageSize, (this.page + 1) * this.pageSize);
		},
		filterItems() {
			const allStatuses = ['draft', 'in_review', 'sending', 'sent', 'scheduled'];
			return allStatuses.map(status => ({
				title: this.getString(status),
				switch: true,
				active: this.newsletterStore.statusFilters.includes(status),
				callback: () => this.toggleStatusFilter(status)
			}));
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		}
	},
	methods: {
		addAttachment() {
			this.$emit('add-attachment');
		},
		pageChanged(page, pageSize) {
			this.page = page;
			this.pageSize = pageSize;
		},
		toggleFilterBottomSheet() {
			this.showFilterBottomSheet = !this.showFilterBottomSheet;
		},
		closeFilterBottomSheet(isOpen) {
			if(!isOpen) {
				this.showFilterBottomSheet = false;
			}
		},
		handleRechargeClose(success) {
			this.showRechargeBottomSheet = false;
			if (success) {
				this.userStore.refreshBusiness(this.userStore.currBusinessId, () => {
					if (this.userStore.currBusiness.open_balance > 0 && this.typedMessage && this.typedMessage.trim()) {
						this.generateEmail();
					}
				});
			}
		},
		toggleStatusFilter(status) {
			const index = this.newsletterStore.statusFilters.indexOf(status);
			if (index > -1) {
				this.newsletterStore.statusFilters.splice(index, 1);
			} else {
				this.newsletterStore.statusFilters.push(status);
			}
			// Reset cache and pagination, then refresh
			this.newsletterStore.businessNewsletters = null;
			this.page = 0;
			this.newsletterStore.getBusinessNewsletters(null, true);
		},
		generateFromTemplate(filledPrompt) {
			this.typedMessage = filledPrompt;
			this.generateEmail();
		},
		generateEmail() {
			if(this.isLoading || !this.typedMessage?.length) {
				return;
			}

			this.isLoading = true;

			if(this.typedMessage.length < 20) {
				this.showNotification('please-provide-more-details');
				this.isLoading = false;
				return;
			}

			const prompt = this.typedMessage;
			const images = [...this.newsletterStore.attachedImages];

			API.init().createNewsletter(this.userStore.currBusinessId, this.typedMessage, images, this.newsletterStore.thinkingMode, (response) => {
				this.isLoading = false;

				if(response.result === 'OK' && response.newsletter_id) {
					this.typedMessage = null;
					this.newsletterStore.attachedImages = [];
					this.newsletterStore.businessNewsletters = null;
					this.page = 0;
					this.newsletterStore.getBusinessNewsletters(() => {
						this.newsletterStore.editingNewsletterId = Number(response.newsletter_id);
					});
				} else {
					// Show specific notification for pending messages
					if (response && response.details === 'newsletter_pending') {
						this.showNotification('newsletter-pending');
						// Restore text and images only if textarea is still empty
						if (!this.typedMessage || this.typedMessage.trim() === '') {
							this.typedMessage = prompt;
							this.newsletterStore.attachedImages = images;
						}
					} else if (response && response.details === 'insufficient_credits') {
						this.showRechargeBottomSheet = true;
						this.showNotification('insufficient-credits-for-newsletter');
					} else {
						this.showNotification('generic-fail');
					}
				}
			});
		},
		openNewsletter(newsletterId) {
			this.newsletterStore.editingNewsletterId = newsletterId;
		}
	}
}
</script>

<style scoped lang="scss">
.selection-view {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 30px;
	width: 100%;
}

.newsletter-header {
	.title {
		font-weight: 600;
		font-size: 24px;
		color: var(--color-text-1);
		line-height: normal;
		text-align: center;
	}

	.subtitle {
		font-weight: 400;
		font-size: 14px;
		color: var(--color-txt-1);
		line-height: normal;
		text-align: center;
		margin-top: 16px;
		line-height: 1.5;
		max-width: 900px;
	}
}

.newsletter-list-card, .newsletter-templates-wrapper {
	max-width: 1440px;
	width: 100%;
}

.newsletters-list {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 20px;
}

@media (max-width: 720px) {
	.newsletters-list {
		grid-template-columns: 1fr;
	}
}
</style>
